/**
 * Archive Confirm Dialog Component
 *
 * Confirmation dialog for triggering archive operation.
 */

import { Archive, Loader2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

interface ArchiveConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  year: number;
  month: number;
  estimatedCount: number;
  dryRun: boolean;
  onConfirm: () => void;
  isArchiving: boolean;
}

/**
 * Format month/year as readable string
 */
function formatMonthYear(year: number, month: number): string {
  const date = new Date(year, month - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Archive Confirm Dialog
 * Confirmation with details before triggering archive
 */
export function ArchiveConfirmDialog({
  open,
  onOpenChange,
  year,
  month,
  estimatedCount,
  dryRun,
  onConfirm,
  isArchiving,
}: ArchiveConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {dryRun ? (
              <Archive className="h-5 w-5 text-blue-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            )}
            {dryRun ? 'Preview Archive' : 'Confirm Archive'}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                {dryRun
                  ? 'This will preview the archive operation without making any changes.'
                  : 'This will permanently archive audit logs and remove them from the database.'}
              </p>

              <div className="rounded-md border p-3 bg-muted/50 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Month:</span>
                  <span className="font-medium">{formatMonthYear(year, month)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Estimated records:</span>
                  <span className="font-medium">{estimatedCount.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Mode:</span>
                  {dryRun ? (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      Dry Run
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-amber-50 text-amber-700 border-amber-200"
                    >
                      Live
                    </Badge>
                  )}
                </div>
              </div>

              {!dryRun && (
                <p className="text-amber-600 font-medium text-sm">
                  This action cannot be undone. Please ensure you have verified the preview first.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isArchiving}
            className={
              dryRun
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-amber-600 text-white hover:bg-amber-700'
            }
          >
            {isArchiving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {dryRun ? 'Running preview...' : 'Archiving...'}
              </>
            ) : (
              <>
                <Archive className="mr-2 h-4 w-4" />
                {dryRun ? 'Run Preview' : 'Archive Now'}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
